import CONVERSAYION_MESSAGE_TYPES from '../constants/conversationMessageTypes';

const { AI_STREAMING, AI_THINKING, ERROR } = CONVERSAYION_MESSAGE_TYPES;

let localKeys = 0;
/** A key for an entry made on the client, unique for the page's life. */
export const localKey = (prefix) => {
  localKeys += 1;
  return `${prefix}-${localKeys}`;
};

// Bubbles that stand in for the answer until it arrives: "Thinking..." placeholders,
// a failed attempt, or the answer being streamed token by token.
const isProvisional = (msg) =>
  !!msg &&
  (msg.message_type === AI_THINKING ||
    msg.message_type === ERROR ||
    msg.message_type === AI_STREAMING);

/**
 * Apply one streamed token of the answer to the conversation.
 * Extends the streaming bubble if one is open, otherwise opens one in place of a
 * thinking/error placeholder (or appends one).
 */
export function applyAnswerDelta(conversation, chunk) {
  const updated = [...conversation];
  const last = updated[updated.length - 1];

  if (last && last.message_type === AI_STREAMING) {
    updated[updated.length - 1] = { ...last, text: `${last.text || ''}${chunk.message}` };
    return updated;
  }

  const bubble = { type: 'ai', text: chunk.message, message_type: AI_STREAMING };
  if (chunk.id) bubble.id = chunk.id;
  if (chunk.thread_id) bubble.thread_id = chunk.thread_id;

  if (last && (last.message_type === AI_THINKING || last.message_type === ERROR)) {
    updated[updated.length - 1] = bubble;
    return updated;
  }
  return [...updated, bubble];
}

/**
 * Apply a complete AI line to the conversation.
 * - A final answer or error replaces every provisional bubble of the current turn (the
 *   answer may have streamed in two pieces around a chart or navigation step).
 * - A thinking placeholder replaces a previous placeholder, but is appended after text
 *   that is already streaming so that text stays visible.
 */
export function applyAiMessage(conversation, messageObject) {
  const updated = [...conversation];

  if (messageObject.message_type === AI_THINKING) {
    const last = updated[updated.length - 1];
    if (last && (last.message_type === AI_THINKING || last.message_type === ERROR)) {
      updated[updated.length - 1] = { ...last, ...messageObject };
      return updated;
    }
    return [...updated, messageObject];
  }

  let carried = {};
  while (updated.length && isProvisional(updated[updated.length - 1])) {
    carried = { ...updated.pop(), ...carried };
  }
  return [...updated, { ...carried, ...messageObject }];
}

// ---- confirm cards ---------------------------------------------------------------------------
// The final answer may ask the user to confirm an action (publish, send, spend):
// action { type: 'request_confirmation', pending_id, action, summary, expires_at }. Once it is
// confirmed the backend appends an assistant line with pending_outcome { pending_id, action,
// status }; a cancel adds no line, so the card keeps that decision on its message.

export const CONFIRMATION_ACTION = 'request_confirmation';

/** True for a confirm-card action that names its pending confirmation. */
export const isConfirmationAction = (action) =>
  !!action &&
  typeof action === 'object' &&
  String(action.type || '').toLowerCase() === CONFIRMATION_ACTION &&
  typeof action.pending_id === 'string' &&
  action.pending_id.trim() !== '';

/** The outcome lines of the conversation by pending id: { [pending_id]: { status, text } }. */
export function pendingOutcomes(conversation) {
  const outcomes = {};
  (conversation || []).forEach((msg) => {
    const outcome = msg?.pending_outcome;
    if (outcome && typeof outcome === 'object' && outcome.pending_id) {
      outcomes[outcome.pending_id] = { status: outcome.status || '', text: msg.text || '' };
    }
  });
  return outcomes;
}

/** The conversation with `patch` merged into the confirmation state of the card for `pendingId`. */
export function markConfirmation(conversation, pendingId, patch) {
  return (conversation || []).map((msg) => (
    isConfirmationAction(msg?.action) && msg.action.pending_id === pendingId
      ? { ...msg, confirmation: { ...(msg.confirmation || {}), ...patch } }
      : msg
  ));
}

/** An outcome line for `pendingId` made on the client (`threadId`, the chat's thread, when known). */
export function outcomeLine(pendingId, { text, status, action = '', threadId = null }) {
  return {
    type: 'ai',
    text,
    message_type: CONVERSAYION_MESSAGE_TYPES.AI,
    pending_outcome: { pending_id: pendingId, action, status },
    clientKey: `outcome-${pendingId}`,
    ...(threadId ? { thread_id: threadId } : {}),
  };
}

/**
 * The conversation with `line`, the outcome line for `pendingId`, right under that card's
 * message, unless it already has one. Nothing else in the conversation moves, and nothing is
 * added when the card is not in the conversation: the chat was replaced meanwhile (a sign-out or
 * sign-in), so the line belongs to a chat that is no longer on screen. The server wrote it into
 * that chat, which shows it on the next load.
 */
export function mergeOutcomeLine(conversation, pendingId, line) {
  const list = conversation || [];
  if (pendingOutcomes(list)[pendingId]) return conversation;
  const card = list.findIndex((msg) => isConfirmationAction(msg?.action) && msg.action.pending_id === pendingId);
  if (card === -1) return conversation;
  return [...list.slice(0, card + 1), line, ...list.slice(card + 1)];
}
